
import React, { useState, useRef, useEffect } from "react";
import { INDUSTRIES, FREQUENCY_OPTIONS } from "@/lib/constants";
import { calculateEarnings } from "@/lib/calculations";
import ResultCard from "./ResultCard";
import { ChevronDown, User, Briefcase, Mail, Send, Globe, Phone } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { submitCalculatorData } from "@/lib/api";
import { useIsMobile, scrollToElement } from "@/hooks/use-mobile";

const Calculator: React.FC = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const resultRef = useRef<HTMLDivElement>(null);
  const [listSize, setListSize] = useState<string>("");
  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [frequency, setFrequency] = useState<number>(1);
  const [result, setResult] = useState<ReturnType<typeof calculateEarnings> | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  
  // Contact form states
  const [showContactForm, setShowContactForm] = useState<boolean>(false);
  const [contactData, setContactData] = useState<{
    name: string;
    company: string;
    position: string;
    email: string;
    website: string;
    phone: string;
  }>({
    name: "",
    company: "",
    position: "",
    email: "",
    website: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

  // Effect to handle scrolling when results appear
  useEffect(() => {
    if (result && isMobile && resultRef.current) {
      // Wait for UI to update before scrolling
      const timer = setTimeout(() => {
        scrollToElement(resultRef.current);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [result, isMobile]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listSize || !selectedIndustry) {
      toast({
        title: "Missing information",
        description: "Please fill out all fields to calculate your potential earnings.",
        variant: "destructive",
      });
      return;
    }
    
    const size = parseInt(listSize, 10);
    if (isNaN(size) || size <= 0) {
      toast({
        title: "Invalid email list size",
        description: "Please enter a valid number for your email list size.",
        variant: "destructive",
      });
      return;
    }
    
    setIsCalculating(true);
    
    const industry = INDUSTRIES.find(ind => ind.id === selectedIndustry);
    
    if (!industry) {
      toast({
        title: "Industry not found",
        description: "Please select a valid industry.",
        variant: "destructive",
      });
      setIsCalculating(false);
      return;
    }
    
    setTimeout(() => {
      const calculationResult = calculateEarnings(size, industry, frequency);
      setResult(calculationResult);
      setIsCalculating(false);
    }, 800);
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProceed = () => {
    setShowContactForm(true);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if all fields are filled
    const requiredFields = ["name", "company", "position", "email", "website", "phone"];
    const missingFields = requiredFields.filter(field => !contactData[field as keyof typeof contactData]);
    
    if (missingFields.length > 0) {
      toast({
        title: "Missing information",
        description: `Please fill out all fields to proceed.`,
        variant: "destructive",
      });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    const submissionData = {
      contact: contactData,
      calculatorData: {
        listSize: parseInt(listSize, 10),
        industry: INDUSTRIES.find(ind => ind.id === selectedIndustry)?.name || "",
        frequency,
        result
      }
    };
    
    try {
      await submitCalculatorData(submissionData);
      
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      toast({
        title: "Request submitted successfully",
        description: "A Vacant Media representative will contact you shortly.",
      });
    } catch (error) {
      console.error("Error submitting data:", error);
      setIsSubmitting(false);
      
      toast({
        title: "Submission error",
        description: "There was a problem submitting your request. Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="glass-morphism rounded-2xl p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="list-size" className="calculator-label">
                Newsletter Subscription List Size
              </label>
              <div className="calculator-input">
                <input
                  id="list-size"
                  type="number"
                  placeholder="e.g. 10000"
                  value={listSize}
                  onChange={(e) => setListSize(e.target.value)}
                  className="w-full bg-transparent outline-none"
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Enter the total number of subscribers on your newsletter list
              </p>
            </div>
            
            <div>
              <label htmlFor="industry" className="calculator-label">
                Industry
              </label>
              <div className="calculator-input relative">
                <select
                  id="industry"
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="w-full bg-transparent appearance-none outline-none pr-10"
                  required
                >
                  <option value="" disabled>
                    Select your industry
                  </option>
                  {INDUSTRIES.map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" />
              </div>
            </div>
            
            <div>
              <label htmlFor="frequency" className="calculator-label">
                Email Frequency (Per Week)
              </label>
              <div className="calculator-input relative">
                <select
                  id="frequency"
                  value={frequency}
                  onChange={(e) => setFrequency(parseInt(e.target.value, 10))}
                  className="w-full bg-transparent appearance-none outline-none pr-10"
                  required
                >
                  {FREQUENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                How many times per week do you want to send emails?
              </p>
            </div>
            
            <button
              type="submit"
              className={`w-full py-4 px-6 rounded-lg text-primary-foreground font-medium transition-all duration-300 
                ${isCalculating 
                  ? "bg-primary/80" 
                  : "bg-primary hover:bg-primary/90 active:scale-[0.98]"}`}
              disabled={isCalculating}
            >
              {isCalculating ? "Calculating..." : "Calculate Newsletter Potential"}
            </button>
          </form>
        </div>
        
        <div className="flex items-center justify-center">
          {result ? (
            <div className="w-full" ref={resultRef}>
              <ResultCard
                minEarnings={result.minEarnings}
                maxEarnings={result.maxEarnings}
                periodLabel={result.periodLabel}
                feeAmount={result.feeAmount}
                emailFeeAmount={result.emailFeeAmount}
                totalEarnings={result.totalEarnings}
                projections={result.projections}
              />
              
              {!showContactForm && !submitSuccess && (
                <div className="mt-8 text-center glass-morphism p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-3">Ready to Maximize Your Newsletter Revenue?</h3>
                  <p className="text-muted-foreground mb-4">Take the next step to unlock your full newsletter marketing potential.</p>
                  <button
                    onClick={handleProceed}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground py-3 px-6 rounded-lg font-medium transition-all active:scale-[0.98] inline-flex items-center gap-2"
                  >
                    Click Here To Proceed <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {showContactForm && !submitSuccess && (
                <div className="mt-8 glass-morphism p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-4">Let's Get Started</h3>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="calculator-label">
                        Your Name
                      </label>
                      <div className="calculator-input flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Smith"
                          value={contactData.name}
                          onChange={handleContactChange}
                          className="w-full bg-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="calculator-label">
                        Company Name
                      </label>
                      <div className="calculator-input flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                        <input
                          id="company"
                          name="company"
                          type="text"
                          placeholder="Your Company"
                          value={contactData.company}
                          onChange={handleContactChange}
                          className="w-full bg-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="website" className="calculator-label">
                        Website
                      </label>
                      <div className="calculator-input flex items-center gap-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <input
                          id="website"
                          name="website"
                          type="text"
                          placeholder="https://example.com"
                          value={contactData.website}
                          onChange={handleContactChange}
                          className="w-full bg-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="position" className="calculator-label">
                        Your Position
                      </label>
                      <div className="calculator-input flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <input
                          id="position"
                          name="position"
                          type="text"
                          placeholder="Marketing Manager"
                          value={contactData.position}
                          onChange={handleContactChange}
                          className="w-full bg-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="calculator-label">
                        Phone Number
                      </label>
                      <div className="calculator-input flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          value={contactData.phone}
                          onChange={handleContactChange}
                          className="w-full bg-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="calculator-label">
                        Email Address
                      </label>
                      <div className="calculator-input flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="you@example.com"
                          value={contactData.email}
                          onChange={handleContactChange}
                          className="w-full bg-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      className={`w-full py-3 px-6 rounded-lg text-primary-foreground font-medium transition-all duration-300 flex items-center justify-center gap-2
                        ${isSubmitting 
                          ? "bg-primary/80" 
                          : "bg-primary hover:bg-primary/90 active:scale-[0.98]"}`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Request"} <Send className="w-4 h-4" />
                    </button>
                  </form>
                </div>
              )}
              
              {submitSuccess && (
                <div className="mt-8 glass-morphism p-6 rounded-xl text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-8 h-8 text-green-600">✓</div>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Request Submitted Successfully!</h3>
                  <p className="text-muted-foreground">
                    Thank you for your interest. A Vacant Media representative will contact you shortly to discuss how we can help maximize your newsletter revenue.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center p-8">
              <h3 className="text-xl font-medium mb-2">See Your Newsletter Potential</h3>
              <p className="text-muted-foreground">
                Fill out the calculator to see how much you could earn with Vacant Media.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calculator;
